/**
 * Visit Counter Service using CountAPI
 * CountAPI is a free, simple service for tracking visits
 * Documentation: https://countapi.xyz/
 */

interface VisitStats {
  total: number
  today: number
}

class VisitCounterService {
  private namespace = 'evoltbay-admin-dashboard'
  private key = 'total-visits'
  private baseUrl = 'https://api.countapi.xyz'

  /**
   * Get today's date key for tracking daily visits
   */
  private getTodayKey(): string {
    return `today-${new Date().toISOString().split('T')[0]}`
  }

  /**
   * Increment and get total visit count
   */
  async trackVisit(): Promise<number> {
    try {
      const response = await fetch(`${this.baseUrl}/hit/${this.namespace}/${this.key}`)
      const data = await response.json()
      return data.value || 0
    } catch (error) {
      console.error('Failed to track visit:', error)
      return 0
    }
  }

  /**
   * Track today's visit
   */
  async trackTodayVisit(): Promise<number> {
    try {
      const todayKey = this.getTodayKey()
      const response = await fetch(`${this.baseUrl}/hit/${this.namespace}/${todayKey}`)
      const data = await response.json()
      return data.value || 0
    } catch (error) {
      console.error('Failed to track today visit:', error)
      return 0
    }
  }

  /**
   * Get visit statistics without incrementing
   */
  async getVisitStats(): Promise<VisitStats> {
    try {
      const todayKey = this.getTodayKey()
      const [totalResponse, todayResponse] = await Promise.all([
        fetch(`${this.baseUrl}/get/${this.namespace}/${this.key}`).catch(() => ({ json: () => ({ value: 0 }) })),
        fetch(`${this.baseUrl}/get/${this.namespace}/${todayKey}`).catch(() => ({ json: () => ({ value: 0 }) }))
      ])

      const totalData = await totalResponse.json()
      const todayData = await todayResponse.json()

      return {
        total: totalData.value || 0,
        today: todayData.value || 0
      }
    } catch (error) {
      console.error('Failed to get visit stats:', error)
      return {
        total: 0,
        today: 0
      }
    }
  }

  /**
   * Track visit and return stats
   */
  async trackAndGetStats(): Promise<VisitStats> {
    try {
      // Track both total and today's visits in parallel
      await Promise.all([
        this.trackVisit(),
        this.trackTodayVisit()
      ])
      
      // Return updated stats
      return await this.getVisitStats()
    } catch (error) {
      console.error('Failed to track and get stats:', error)
      return { total: 0, today: 0 }
    }
  }
}

export const visitCounterService = new VisitCounterService()
